import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useMobileDetection } from '../../hooks/useMobileDetection';
import LogoNav from '../Navigation/LogoNav';
import './AccountLayout.css';

interface AccountLayoutProps {
  children: React.ReactNode;
  activeSection: 'overview' | 'account' | 'reports' | 'pricing';
}

interface NavigationItem {
  id: 'overview' | 'account' | 'reports' | 'pricing';
  label: string;
  icon: string;
  description?: string;
}

const AccountLayout: React.FC<AccountLayoutProps> = ({ children, activeSection }) => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { isMobile, isTablet } = useMobileDetection();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  // Treat tablets as mobile for layout purposes
  const useMobileLayout = isMobile || isTablet;

  const navigationItems: NavigationItem[] = [
    {
      id: 'overview',
      label: 'Overview',
      icon: '',
      description: 'Account dashboard and usage'
    },
    {
      id: 'account',
      label: 'Account Settings',
      icon: '',
      description: 'Security and account management'
    },
    {
      id: 'reports',
      label: 'My Reports',
      icon: '',
      description: 'View and download saved reports'
    },
    {
      id: 'pricing',
      label: 'Plans & Pricing',
      icon: '',
      description: 'Subscription and billing'
    }
  ];

  const handleNavigation = (sectionId: string) => {
    switch (sectionId) {
      case 'overview':
        navigate('/account/overview');
        break;
      case 'account':
        navigate('/account/settings');
        break;
      case 'reports':
        navigate('/account/reports');
        break;
      case 'pricing':
        navigate('/account/pricing');
        break;
      default:
        navigate('/account/overview');
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="account-layout">
      {!useMobileLayout && <LogoNav />}
      
      <div className="account-layout-container">
        {/* Sidebar Navigation - hidden on mobile and tablet */}
        {!useMobileLayout && (
          <aside className="account-sidebar">
            <div className="account-sidebar-header">
              <h2>Account</h2>
            </div>
            
            <nav className="account-nav">
              <div className="account-nav-section">
                {navigationItems.map((item) => (
                  <button
                    key={item.id}
                    className={`account-nav-item ${activeSection === item.id ? 'active' : ''}`}
                    onClick={() => handleNavigation(item.id)}
                  >
                    <div className="account-nav-item-icon">{item.icon}</div>
                    <div className="account-nav-item-content">
                      <div className="account-nav-item-label">{item.label}</div>
                      {item.description && (
                        <div className="account-nav-item-description">{item.description}</div>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              {/* Logout Section */}
              <div className="account-nav-footer">
                <button
                  className="account-nav-item logout-item"
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                >
                  <div className="account-nav-item-icon">
                    {isLoggingOut ? '' : ''}
                  </div>
                  <div className="account-nav-item-content">
                    <div className="account-nav-item-label">
                      {isLoggingOut ? 'Signing Out...' : 'Sign Out'}
                    </div>
                    <div className="account-nav-item-description">
                      Return to main application
                    </div>
                  </div>
                </button>
              </div>
            </nav>
          </aside>
        )}

        {/* Main Content Area */}
        <main className="account-main-content">
          <div className="account-content-wrapper">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AccountLayout; 